import React from "react";
import { Link } from "react-router-dom";
import Logo from "@/components/Logo";
import { ArrowLeft } from "lucide-react";

/**
 * Shared chrome for legal / policy pages (Terms, Refund, etc.).
 * Keeps typography and navigation consistent without duplicating layout.
 */
export default function LegalPage({ eyebrow, title, lastUpdated, children, testId }) {
  return (
    <div className="min-h-screen bg-white dark:bg-zinc-950 text-zinc-900 dark:text-zinc-100" data-testid={testId}>
      <header className="border-b border-zinc-200 dark:border-zinc-800">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <Logo size={28} />
            <span className="font-sans font-bold tracking-tight text-lg">
              Resy<span className="text-blue-600">Note</span>
            </span>
          </Link>
          <Link
            to="/"
            className="text-xs font-mono text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-50 flex items-center gap-1.5"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back home
          </Link>
        </div>
      </header>

      <article className="max-w-3xl mx-auto px-6 py-16 md:py-20">
        <div className="mb-10">
          {eyebrow && (
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-blue-600">
              {eyebrow}
            </div>
          )}
          <h1 className="text-4xl sm:text-5xl font-sans font-bold tracking-tight mt-2">
            {title}
          </h1>
          {lastUpdated && (
            <div className="mt-3 text-xs font-mono text-zinc-500">
              Last updated: {lastUpdated}
            </div>
          )}
        </div>

        <div className="legal-content font-body text-[15px] leading-relaxed text-zinc-700 dark:text-zinc-300 space-y-6">
          {children}
        </div>

        <div className="mt-16 pt-6 border-t border-zinc-200 dark:border-zinc-800 flex flex-wrap gap-4 text-xs font-mono text-zinc-500">
          <Link to="/contact" className="hover:text-zinc-900 dark:hover:text-zinc-50">Contact</Link>
          <span>·</span>
          <Link to="/terms" className="hover:text-zinc-900 dark:hover:text-zinc-50">Terms & Conditions</Link>
          <span>·</span>
          <Link to="/refund-policy" className="hover:text-zinc-900 dark:hover:text-zinc-50">Refund Policy</Link>
        </div>
      </article>
    </div>
  );
}

/** Utility block components used by the legal pages */
export function Section({ n, title, children }) {
  return (
    <section className="space-y-3">
      <h2 className="text-lg sm:text-xl font-sans font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">
        {n ? <span className="text-zinc-400 font-mono mr-2">{n}.</span> : null}
        {title}
      </h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

export function Bullets({ items }) {
  return (
    <ul className="list-disc pl-6 space-y-1.5 marker:text-zinc-400">
      {items.map((it, i) => (
        <li key={i}>{it}</li>
      ))}
    </ul>
  );
}
